import { createAction, createReducer, on } from '@ngrx/store';
import { isNullOrUndefined, isDefined, mapWhenDefined, ensureArray } from '@essent/core-utils';
export { ensureArray, isArrayDefined, isDefined, isNullOrUndefined } from '@essent/core-utils';
import { merge, of } from 'rxjs';
import { filter, concatMap, mergeMap, switchMap, takeUntil, map, catchError, exhaustMap } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import { HttpUrlEncodingCodec, HttpParams } from '@angular/common/http';
import { isArray, isPlainObject, camelCase as camelCase$1, snakeCase as snakeCase$1, get } from 'lodash';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, NgModule, SkipSelf } from '@angular/core';

function createBaseAction(type) {
    return createAction(type, (params = { payload: null }) => params);
}

/**
 * Converts null values of an object (1 level deep) to undefined for consistency purposes.
 */
function convertNullMembersToUndefined(object) {
    Object.keys(object)
        .filter(key => isNullOrUndefined(object[key]))
        .forEach(key => {
        object[key] = undefined;
    });
    return object;
}

function convertStringToEnumValue(type, value, defaultValue) {
    return (Object.keys(type)
        .map(key => type[key])
        .find(typeValue => typeValue === value) || defaultValue);
}

/**
 *
 *  Each `effect` of a core-module handles the `actions` based on the passed `requestStrategy`.
 *  The strategy applies to cases where multiple actions **OF THE SAME TYPE** are dispatched.
 *
 *  Available strategies:
 *
 *  `CANCEL` (default)
 *  ```
 *  Request - X
 *  Request - X
 *  Request - X
 *  Request -------- Response
 *  ```
 *
 * `PARALLEL`
 * ```
 * Request ---------- Response
 * Request ------- Response
 * Request ---------- Response
 * Request ------------- Response
 * ```
 *
 * `SEQUENCE`
 * ```
 * Request --- Response
 * ----------- Request -------- Response
 * ---------------------------- Request -------- Response
 * ```
 *  `IGNORE` (default)
 *  ```
 *  Request -------- Response
 *  Request - X
 *  Request - X
 *  Request - X
 *  ```
 *
 * ##### Example CANCEL:
 * A page in the application displays 1 Account. It is possible for the user to switch from account A to B before A is successfully
 * retrieved. In this case you would like to cancel the first request so its response does not interfere with the second response.
 *
 * ```typescript
 * store.dispatch(new GetAccount({ // <--- Cancelled by the next action
 *     payload: {
 *        id: '1',
 *        requestStrategy: RequestStrategy.CANCEL // <--- Optional
 *
 *     }
 * }));
 *
 * store.dispatch(new GetAccount({ // <--- Completed
 *     payload: {
 *         id: '2',
 *         requestStrategy: RequestStrategy.CANCEL // <--- Optional
 *     }
 * }));
 * ```
 *
 * ##### Example PARALLEL:
 * A page in the application displays 2 Accounts. You don't want the first request to be cancelled if the second is dispatched.
 *
 * ```typescript
 * store.dispatch(new GetAccount({ // <--- Completed
 *     payload: {
 *        id: '1',
 *        requestStrategy: RequestStrategy.PARALLEL
 *     }
 * }));
 *
 * store.dispatch(new GetAccount({ // <--- Completed
 *     payload: {
 *        id: '2',
 *        requestStrategy: RequestStrategy.PARALLEL
 *    }
 * }));
 * ```
 *
 * If these calls should also be ignored like in the previous example, passing an `actionId` would help solving this issue.
 */
var RequestStrategy;
(function (RequestStrategy) {
    RequestStrategy["PARALLEL"] = "Parallel";
    RequestStrategy["SEQUENCE"] = "Sequence";
    RequestStrategy["CANCEL"] = "Cancel";
    RequestStrategy["IGNORE"] = "Ignore";
})(RequestStrategy || (RequestStrategy = {}));

/**
 * Function used in each effect that implements the [RequestStrategy](./enumerations.html#RequestStrategy)
 */
function handleRequestWithStrategy(action$, requestHandler) {
    const sequence$ = action$.pipe(filter(({ requestStrategy }) => requestStrategy === RequestStrategy.SEQUENCE));
    const parallel$ = action$.pipe(filter(({ requestStrategy }) => requestStrategy === RequestStrategy.PARALLEL));
    const cancel$ = action$.pipe(filter(({ requestStrategy }) => requestStrategy === RequestStrategy.CANCEL));
    return merge(sequence$.pipe(concatMap(action => requestHandler(action))), parallel$.pipe(mergeMap(action => requestHandler(action))), cancel$.pipe(switchMap(action => {
        return requestHandler(action).pipe(takeUntil(merge(sequence$, parallel$)));
    })));
}

class CustomParamsEncoder extends HttpUrlEncodingCodec {
    constructor() {
        super(...arguments);
        this.encodeValue = (key) => super.encodeValue(key).replace(/\+/gi, '%2B');
    }
}

/**
 * Converts key/value pairs to [HttpParams]{@link https://angular.io/api/common/http/HttpParams}
 */
function toHttpParams(params) {
    const definedObject = Object.keys(params).reduce((obj, key) => {
        const value = params[key];
        if (value instanceof Array) {
            obj[key] = value.filter(arrayVal => arrayVal !== null && arrayVal !== undefined && arrayVal !== '');
        }
        else if (value !== null && value !== undefined && value !== '') {
            obj[key] = value;
        }
        return obj;
    }, {});
    return new HttpParams({
        fromObject: definedObject,
        encoder: new CustomParamsEncoder()
    });
}

const recursiveKeysConvertCase = (convertFunc) => (input) => {
    if (isArray(input)) {
        return input.map(recursiveKeysConvertCase(convertFunc));
    }
    else if (isPlainObject(input)) {
        const newObj = {};
        Object.keys(input).forEach(key => {
            const newKey = convertFunc(key);
            newObj[newKey] = recursiveKeysConvertCase(convertFunc)(input[key]);
        });
        return newObj;
    }
    return input;
};

const recursiveKeysCamelCase = recursiveKeysConvertCase(camelCase$1);
const camelCase = (value) => recursiveKeysCamelCase(value);
const toCamelcase = () => (source$) => source$.pipe(map(value => camelCase(value)));
const camelCaseWhen = (condition) => condition ? toCamelcase() : (source$) => source$;

const recursiveKeysSnakeCase = recursiveKeysConvertCase(snakeCase$1);
const snakeCase = (value) => recursiveKeysSnakeCase(value);
const toSnakeCase = () => (source$) => source$.pipe(map(value => snakeCase(value)));
const snakeCaseWhen = (value, perform) => (perform ? snakeCase(value) : value);

const isApiActionsObject = (input) => {
    const typedInput = input;
    return (isDefined(typedInput) &&
        isDefined(typedInput.requestAction) &&
        isDefined(typedInput.successAction) &&
        isDefined(typedInput.errorAction) &&
        isDefined(typedInput.clearAction));
};
/**
 * Creates a set of API actions.
 * ### Usage
 * ```typescript
 * const actions = createApiActions<PutCustomerDetailsRequest, Customer>('[PutCustomerActions] Put customer');
 * // actions: { requestAction, successAction, errorAction, clearAction }
 *
 * // Effect configuration is directly compatible:
 * const effectConfiguration: EffectConfiguration<PutCustomerDetailsRequest, Customer> = actions;
 * ```
 * @param actionTypePrefix Prefix for action type. For example: `[PutCustomerActions] Put customer`
 * @returns Standard set of API actions to be used in reducers / effects
 */
const createApiActions = (actionTypePrefix) => ({
    requestAction: createBaseAction(actionTypePrefix),
    successAction: createBaseAction(`${actionTypePrefix} success`),
    errorAction: createBaseAction(`${actionTypePrefix} error`),
    clearAction: createBaseAction(`${actionTypePrefix} clear`)
});

function baseEffectFactory(actions$, requestService, service, config) {
    return new BaseEffect(actions$, requestService, service, config);
}
class BaseEffect {
    ngrxOnIdentifyEffects() {
        return this.configuration.requestAction.type;
    }
    constructor(actions$, requestService, dataService, configuration) {
        this.actions$ = actions$;
        this.requestService = requestService;
        this.dataService = dataService;
        this.configuration = configuration;
        this.effect$ = createEffect(() => this.requestService.handle(
        // eslint-disable-next-line rxjs/no-unbound-methods
        this.actions$.pipe(ofType(this.configuration.requestAction)), ({ type, payload, ...params }) => this.dataService.call$(payload, params).pipe(map(succesPayload => this.configuration.successAction({
            actionId: params.actionId,
            payload: succesPayload
        })), catchError((errorPayload) => of(this.configuration.errorAction({
            actionId: params.actionId,
            payload: errorPayload
        }))))));
    }
}

/**
 * Const for providing CLIENT_KEY
 */
const CLIENT_KEY = new InjectionToken('client_key');
/**
 * Const for providing CLIENT_SECRET
 */
const CLIENT_SECRET = new InjectionToken('client_secret');

const DEFAULT_REQUEST_STRATEGY = new InjectionToken('default_request_strategy');

class HandleRequestService {
    constructor(defaultRequestStrategy) {
        this.defaultRequestStrategy = defaultRequestStrategy;
    }
    handle(action$, requestHandler) {
        action$ = action$.pipe(map(action => action.requestStrategy
            ? action
            : {
                ...action,
                requestStrategy: this.defaultRequestStrategy || RequestStrategy.CANCEL
            }));
        const sequence$ = action$.pipe(filter(({ requestStrategy }) => requestStrategy === RequestStrategy.SEQUENCE));
        const parallel$ = action$.pipe(filter(({ requestStrategy }) => requestStrategy === RequestStrategy.PARALLEL));
        const cancel$ = action$.pipe(filter(({ requestStrategy }) => requestStrategy === RequestStrategy.CANCEL));
        const ignore$ = action$.pipe(filter(({ requestStrategy }) => requestStrategy === RequestStrategy.IGNORE));
        return merge(sequence$.pipe(concatMap(action => requestHandler(action))), parallel$.pipe(mergeMap(action => requestHandler(action))), ignore$.pipe(exhaustMap(action => requestHandler(action))), cancel$.pipe(switchMap(action => {
            return requestHandler(action).pipe(takeUntil(merge(sequence$, parallel$, ignore$)));
        })));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: HandleRequestService, deps: [{ token: DEFAULT_REQUEST_STRATEGY, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: HandleRequestService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: HandleRequestService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: RequestStrategy, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DEFAULT_REQUEST_STRATEGY]
                }] }] });

function getEffectsProviders(effectsConfig, configuration, service, effects, includeServiceProvider) {
    const configProvider = { provide: effectsConfig, useValue: configuration };
    const effectProvider = {
        provide: effects,
        useFactory: baseEffectFactory,
        deps: [Actions, HandleRequestService, service, effectsConfig]
    };
    return [...(includeServiceProvider ? [service] : []), configProvider, effectProvider];
}
/**
 * Creates an effect, using the actions object and the service as input.
 * Includes the necessary providers so we avoid all boilerplate.
 *
 * ### Usage
 *
 * ```ts
 * const apiActions = createApiActions<Request, Response>('[SomeState] Some Action');
 * const effectWithProviders = createEffectWithProviders(apiActions, SomeService);
 * ```
 *
 * @param includeServiceProvider Whether to include the service itself as a provider in the resulting array
 */
function createEffectWithProviders(actions, service, includeServiceProvider = true) {
    class EffectsConfig {
    }
    class Effects {
    }
    const effectsProviders = getEffectsProviders(EffectsConfig, actions, service, Effects, includeServiceProvider);
    return {
        EffectType: Effects,
        providers: effectsProviders
    };
}
/**
 * Combines several EffectWithProvider objects together for ease of use. This works both with EffectProvider objects as with api objects.
 * ### Example usage
 *
 * ```ts
 * const { EFFECTS, PROVIDERS } = combineEffectsWithProviders(
 *      getAddressCheckApi,
 *      getContractsEffectsConfig,
 *      getInstallationAddressesEffectsConfig,
 *      getInstallationDetailsApi
 * );
 * ```
 */
function combineEffectsWithProviders(...effectWithProviders) {
    return effectWithProviders.reduce((prev, current) => ({
        EFFECTS: [...prev.EFFECTS, current.EffectType],
        PROVIDERS: [...prev.PROVIDERS, ...current.providers]
    }), { EFFECTS: [], PROVIDERS: [] });
}

/**
 * Creates an API object containing the following items:
 * - `EffectType` (the effect)
 * - `providers` (Provider array necessary for this effect)
 * - `actions` (actions object)
 * - `requestAction`, `errorAction`, ... are exposed seperately as well, but identical to the `actions` object.
 *
 * ### Usage
 *
 * ```ts
 * // get-address-check.config.ts
 * export const getAddressCheckApi = createApi('[AddressCheckState] Get address check', GetAddressCheckService);
 *
 * // get-address-check.actions.ts
 * export const {
 *   requestAction: getAddressCheck,
 *   successAction: getAddressCheckSuccess,
 *   errorAction: getAddressCheckError,
 *   clearAction: getAddressCheckClear,
 *   actions: getAddressCheckActions
 * } = getAddressCheckApi;
 * ```
 *
 * And in the `module.ts` that should import them:
 * ```ts
 * const { EFFECTS, PROVIDERS } = combineEffectsWithProviders(
 *   getAddressCheckApi,
 *   getContractsApi,
 *   getInstallationAddressesApi,
 *   getInstallationDetailsApi
 * );
 * ```
 *
 * @param actionTypePrefix String like `[SomeState] Some action` that is used to create action types
 * @param service Service that implements `BaseService<Request, Response>`.
 * @param createServiceProvider Whether to include the service provider in the resulting providers array for ease of use.
 */
const createApi = (actionTypePrefix, service, createServiceProvider = true) => {
    const actions = createApiActions(actionTypePrefix);
    const { EffectType, providers } = createEffectWithProviders(actions, service, createServiceProvider);
    return {
        EffectType,
        providers,
        actions,
        ...actions
    };
};

/** Action type helper for `createNamedApiActions`. */
const actionTypes = () => ({});
/**
 * Creates a set of API actions, including generated names.
 *
 * ### Usage
 * ```typescript
 * const actions = createNamedApiActions('[PutCustomerActions] Put customer', actionTypes<PutCustomerDetailsRequest, Customer>());
 * // actions: { requestAction, successAction, errorAction, clearAction,
 * //    putCustomer, putCustomerSuccess, putCustomerError, putCustomerClear }
 *
 * // Suggestion to export all actions by name:
 * export const { putCustomer, putCustomerSuccess, putCustomerError, putCustomerClear } = actions;
 *
 * // Effect configuration is also directly compatible:
 * const effectConfiguration: EffectConfiguration<PutCustomerDetailsRequest, Customer> = actions;
 * ```
 * @param actionTypePrefix Prefix for action type. For example: `[PutCustomerActions] Put customer`
 * @returns Standard set of API actions to be used in reducers / effects. In case of `[PutCustomerActions] Put customer`:
 * `putCustomer`, `putCustomerSuccess`, `putCustomerError` and `putCustomerClear` as well.
 */
const createNamedApiActions = (actionTypePrefix, _types) => {
    const extractedActionName = actionTypePrefix.replace(/^\[.*\] (.*)$/, '$1');
    const extractedActionNameCamelCase = camelCase$1(extractedActionName);
    const apiActions = createApiActions(actionTypePrefix);
    const namedActions = {
        [extractedActionNameCamelCase]: apiActions.requestAction,
        [`${extractedActionNameCamelCase}Success`]: apiActions.successAction,
        [`${extractedActionNameCamelCase}Error`]: apiActions.errorAction,
        [`${extractedActionNameCamelCase}Clear`]: apiActions.clearAction
    };
    const actionsObject = { [`${extractedActionNameCamelCase}Actions`]: apiActions };
    return {
        ...actionsObject,
        ...namedActions,
        apiActions
    };
};

/**
 * Creates an API object containing the following items:
 * - `EffectType` (the effect)
 * - `providers` (Provider array necessary for this effect)
 * - `...Actions` and `apiActions` (same actions object)
 * -
 *
 * ### Usage
 *
 * ```ts
 * // get-address-check.config.ts
 * export const getAddressCheckApi = createApi('[AddressCheckState] Get address check', GetAddressCheckService);
 *
 * // get-address-check.actions.ts
 * export const {
 *   getAddressCheck,
 *   getAddressCheckSuccess,
 *   getAddressCheckError,
 *   getAddressCheckClear,
 *   getAddressCheckActions
 * } = getAddressCheckApi;
 * ```
 *
 * And in the `module.ts` that should import them:
 * ```ts
 * const { EFFECTS, PROVIDERS } = combineEffectsWithProviders(
 *   getAddressCheckApi,
 *   getContractsApi,
 *   getInstallationAddressesApi,
 *   getInstallationDetailsApi
 * );
 * ```
 *
 * @param actionTypePrefix String like `[SomeState] Some action` that is used to create action types
 * @param service Service that implements `BaseService<Request, Response>`.
 * @param createServiceProvider Whether to include the service provider in the resulting providers array for ease of use.
 */
const createNamedApi = (actionTypePrefix, service, createServiceProvider = true) => {
    const namedApiActions = createNamedApiActions(actionTypePrefix);
    const { EffectType, providers } = createEffectWithProviders(namedApiActions.apiActions, service, createServiceProvider);
    return {
        ...namedApiActions,
        EffectType,
        providers
    };
};

const accountIdHeaderName = 'Account-ID';

const defaultOptions = {
    snakeCaseParams: true,
    camelCaseResponse: true,
    snakeCaseBody: true,
    defaultHeaders: {
        Accept: 'application/json'
    }
};
/**
 * Simple handler for constructing the url
 */
const constructUrl = ({ getUrl }, payload, apiPrefix = '') => `${apiPrefix}${typeof getUrl === 'string' ? getUrl : getUrl(payload)}`;
/**
 * Simple handler to generate headers
 */
const handleHeaders = ({ getHeaders, defaultHeaders, accountIdHeaderExtractor }, payload, params) => ({
    ...(params?.noCache ? { 'Cache-Control': 'max-age=0' } : undefined),
    ...defaultHeaders,
    ...(isDefined(getHeaders) ? getHeaders(payload) : {}),
    ...(isDefined(accountIdHeaderExtractor) ? { [accountIdHeaderName]: accountIdHeaderExtractor(payload) } : {})
});
/**
 * Simple handler to generate params.
 * If `options.snakeCaseParams` is **not** `false`, will snakeCase params
 */
const handleParams = ({ getParams, snakeCaseParams }, payload) => mapWhenDefined(getParams, paramFn => toHttpParams(snakeCaseWhen(paramFn(payload), snakeCaseParams !== false)));
const getServiceCallOptions = (options, payload, params) => ({
    params: handleParams(options, payload),
    headers: handleHeaders(options, payload, params)
});
/**
 * Factory for making a service handler.
 * This makes sure we construct this once at construction of the service, not every time we make a call
 * The post / patch / put calls will have a body available, the get/delete do not.
 */
const getHttpClientServiceCall = (type, options, httpClient, apiPrefix) => {
    const enrichedOptions = {
        ...defaultOptions,
        ...options
    };
    switch (type) {
        case 'post':
        case 'patch':
        case 'put':
            return (payload, params) => {
                const getBody = enrichedOptions.getBody;
                const body = snakeCaseWhen(getBody(payload), enrichedOptions.snakeCaseBody);
                return httpClient[type](constructUrl(enrichedOptions, payload, apiPrefix), body, getServiceCallOptions(enrichedOptions, payload, params)).pipe(camelCaseWhen(enrichedOptions.camelCaseResponse), enrichedOptions.extractFromNestedKey
                    ? map(response => get(response, enrichedOptions.extractFromNestedKey))
                    : map(x => x));
            };
        case 'get':
            return (payload, params) => httpClient[type](constructUrl(enrichedOptions, payload, apiPrefix), getServiceCallOptions(enrichedOptions, payload, params)).pipe(camelCaseWhen(enrichedOptions.camelCaseResponse), enrichedOptions.extractFromNestedKey
                ? map(response => get(response, enrichedOptions.extractFromNestedKey))
                : map(x => x));
        case 'delete':
            return (payload, params) => httpClient[type](constructUrl(enrichedOptions, payload, apiPrefix), getServiceCallOptions(enrichedOptions, payload, params)).pipe(camelCaseWhen(enrichedOptions.camelCaseResponse), enrichedOptions.extractFromNestedKey
                ? map(response => get(response, enrichedOptions.extractFromNestedKey))
                : map(x => x));
    }
};
const API_PREFIX = new InjectionToken('API_PREFIX', {
    factory: () => ''
});
/**
 * Creates service definition. This function generates a single call towards an endpoint based on the serviceoptions provided.
 * @param type method of the request
 * @param _genericServiceType type util handler that can be provided with the function `serviceTypes<X, Y>()`, only used for type inference.
 */
const createService = (type, _genericServiceType, options) => {
    const { getUrl, version } = options;
    class Service {
        constructor(httpClient, apiPrefix) {
            this.httpClient = httpClient;
            this.apiPrefix = apiPrefix;
            this.apiVersion = version;
            this.endpoint = getUrl;
            this.call$ = getHttpClientServiceCall(type, options, this.httpClient, this.apiPrefix);
        }
        static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: Service, deps: [{ token: i1.HttpClient }, { token: API_PREFIX }], target: i0.ɵɵFactoryTarget.Injectable }); }
        static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: Service }); }
    }
    i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: Service, decorators: [{
                type: Injectable
            }], ctorParameters: () => [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [API_PREFIX]
                    }] }] });
    return Service;
};
const serviceTypes = () => undefined;
/**
 * @param actionTypePrefix Action string used for constructing actions. For example: `[Address state] get address`.
 * @param serviceType method of the request
 * @param _genericServiceType type util handler that can be provided with the function `serviceTypes<X, Y>()`, only used for type inference.
 * @param options Service options. in case of methods that can include a body, it allows for a `getBody` method.
 * Several options have `snakeCase` or `camelCase` options - these are _enabled_ by default.
 *
 * ## Note
 * Returns `options` in the results directly for testing purposes
 *
 * ## Example usage:
 * ```ts
 * const apiVersion = '2';
 * export const updateOnlineAccountApi = createServiceApi(
 *     '[ConsumerState] Update online account',
 *     'post',
 *     serviceTypes<UpdateOnlineAccountPayload, ConsumerResponse>(),
 *     {
 *         getParams: ({ onlineAccountId }) => ({ onlineAccountId }),
 *         getUrl: `/consumer/onlineaccount/v${apiVersion}`,
 *         version: apiVersion,
 *         getBody: ({ onlineAccount }) => onlineAccount
 *     }
 * );
 * ```
 */
const createServiceApi = (actionTypePrefix, serviceType, _genericServiceType, options) => ({
    ...createNamedApi(actionTypePrefix, createService(serviceType, _genericServiceType, options), true),
    options
});

const CLEAR_ALL = '[State] Clear all';
const clearAll = createBaseAction(CLEAR_ALL);

class CommonCoreModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('CommonCoreModule is imported more than once.');
        }
    }
    static forRoot(config) {
        return {
            ngModule: CommonCoreModule,
            providers: [
                {
                    provide: DEFAULT_REQUEST_STRATEGY,
                    useValue: config ? config.defaultRequestStrategy : undefined
                }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CommonCoreModule, deps: [{ token: CommonCoreModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.7", ngImport: i0, type: CommonCoreModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CommonCoreModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.7", ngImport: i0, type: CommonCoreModule, decorators: [{
            type: NgModule
        }], ctorParameters: () => [{ type: CommonCoreModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }] });

var CaseStatus;
(function (CaseStatus) {
    CaseStatus["INPROGRESS"] = "INPROGRESS";
    CaseStatus["COMPLETED"] = "COMPLETED";
    CaseStatus["INCOOLDOWN"] = "INCOOLDOWN";
})(CaseStatus || (CaseStatus = {}));

var Channel;
(function (Channel) {
    Channel["TELEPHONE"] = "telephone";
    Channel["BRIEF"] = "brief";
    Channel["RETURN_POST"] = "returnpost";
    Channel["FAX"] = "fax";
    Channel["FORMS"] = "forms";
    Channel["MIJN_ESSENT"] = "mijnessent_nl";
    Channel["MAIL_WEBFORM"] = "e-mail_webform";
    Channel["VISIT"] = "visit";
    Channel["EXTERN"] = "extern";
    Channel["OPEN_ENV_WEBFORM"] = "open_env_webform";
    Channel["CLOSED_ENV_WEBFORM"] = "closed_env_webform";
    Channel["SOCIAL_MEDIA"] = "social_media";
    Channel["INTERNAL_ADVICE"] = "intern_advice";
    Channel["SMS"] = "sms";
    Channel["CHAT"] = "chat";
    Channel["TELEFONIEBOT"] = "telefoniebot";
    Channel["CHATBOT"] = "chatbot";
})(Channel || (Channel = {}));

var Direction;
(function (Direction) {
    Direction["DELIVERY"] = "LVR";
    Direction["REDELIVERY"] = "TLV";
    Direction["COMBINATION"] = "CMB";
})(Direction || (Direction = {}));

var DirectionTariff;
(function (DirectionTariff) {
    DirectionTariff["SUPPLY_LOW"] = "supply_low";
    DirectionTariff["SUPPLY_NORMAL"] = "supply_normal";
    DirectionTariff["BACK_SUPPLY_LOW"] = "return_supply_low";
    DirectionTariff["BACK_SUPPLY_NORMAL"] = "return_supply_normal";
})(DirectionTariff || (DirectionTariff = {}));
/**
 * @deprecated The use of DirectionTariffZone is deprecated. Use DirectionTariff instead.
 */
var DirectionTariffZone;
(function (DirectionTariffZone) {
    DirectionTariffZone["SUPPLY_LOW"] = "supply_low";
    DirectionTariffZone["SUPPLY_NORMAL"] = "supply_normal";
    DirectionTariffZone["BACK_SUPPLY_LOW"] = "return_supply_low";
    DirectionTariffZone["BACK_SUPPLY_NORMAL"] = "return_supply_normal";
})(DirectionTariffZone || (DirectionTariffZone = {}));

var DocumentType;
(function (DocumentType) {
    DocumentType["INVOICE"] = "invoice";
    DocumentType["FIRST_REMINDER"] = "first_reminder";
    DocumentType["SECOND_REMINDER"] = "second_reminder";
    DocumentType["LAST_REMINDER"] = "last_reminder";
    DocumentType["LAST_POSSIBLE_PAYMENT"] = "last_possible_payment";
    DocumentType["DEACTIVATION"] = "deactivation";
    DocumentType["CONFIRMATION"] = "confirmation"; // installment plan
})(DocumentType || (DocumentType = {}));

var FinancialItemStatus;
(function (FinancialItemStatus) {
    FinancialItemStatus["OPEN"] = "open";
    FinancialItemStatus["CLEARED"] = "cleared";
})(FinancialItemStatus || (FinancialItemStatus = {}));

var FinancialItemType;
(function (FinancialItemType) {
    FinancialItemType["ADMINISTRATION_COSTS"] = "administration_costs";
    FinancialItemType["AUTOMATIC_CLEARING"] = "automatic_clearing";
    FinancialItemType["BANK_RETURN"] = "bank_return";
    FinancialItemType["BUDGET_BILL_PLAN"] = "budget_bill_plan";
    FinancialItemType["CLEARING_DOCUMENT"] = "clearing_document";
    FinancialItemType["COLLECTIVE_BILL"] = "collective_bill";
    FinancialItemType["COLLECTIVE_COSTS"] = "collective_costs";
    FinancialItemType["CONTRACT_BREACH_COSTS"] = "contract_breach_costs";
    FinancialItemType["CORRECTION"] = "correction";
    FinancialItemType["CREDIT_NOTE"] = "credit_note";
    FinancialItemType["CREDIT_TRANSFER"] = "credit_transfer";
    FinancialItemType["DIRECT_DEBIT"] = "direct_debit";
    FinancialItemType["DUNNING_COSTS"] = "dunning_costs";
    FinancialItemType["FINAL_INVOICE"] = "final_invoice";
    FinancialItemType["FINANCIAL_COMPENSATION"] = "financial_compensation";
    FinancialItemType["FINANCIAL_POSTING"] = "financial_posting";
    FinancialItemType["INSTALLMENT"] = "installment";
    FinancialItemType["INSTALLMENT_PLAN"] = "installment_plan";
    FinancialItemType["INTEREST_INVOICE"] = "interest_invoice";
    FinancialItemType["INTERIM"] = "interim";
    FinancialItemType["INVOICE"] = "invoice";
    FinancialItemType["LAST_REMINDER_COSTS"] = "last_reminder_costs";
    FinancialItemType["MANUAL_INVOICE"] = "manual_invoice";
    FinancialItemType["MANUAL_PAYMENT"] = "manual_payment";
    FinancialItemType["MASS_POSTING"] = "mass_posting";
    FinancialItemType["MIGRATION_DOCUMENT"] = "migration_document";
    FinancialItemType["MULTISITE_INVOICE"] = "multisite_invoice";
    FinancialItemType["NOTE"] = "note";
    FinancialItemType["REFUND_FINANCIAL_INCENTIVE"] = "refund_financial_incentive";
    FinancialItemType["REVERSAL"] = "reversal";
    FinancialItemType["REVERSAL_BILL"] = "reversal_bill";
    FinancialItemType["REVERSAL_CLEARING"] = "reversal_clearing";
    FinancialItemType["REVERSAL_DOCUMENT"] = "reversal_document";
    FinancialItemType["SECOND_REMINDER_COSTS"] = "second_reminder_costs";
    FinancialItemType["SECURITY_DEPOSIT"] = "security_deposit";
    FinancialItemType["SETTLEMENT"] = "settlement";
    FinancialItemType["WRITE_OFF"] = "write_off";
    FinancialItemType["YEARLY_INVOICE"] = "yearly_invoice"; // Jaarafrekening
})(FinancialItemType || (FinancialItemType = {}));

var Gender;
(function (Gender) {
    Gender["MALE"] = "male";
    Gender["FEMALE"] = "female";
    Gender["OTHER"] = "other";
})(Gender || (Gender = {}));

var PaymentMethod;
(function (PaymentMethod) {
    PaymentMethod["DIRECT_DEBIT"] = "direct_debit";
    PaymentMethod["CREDIT_TRANSFER"] = "credit_transfer";
    PaymentMethod["MANUAL_TRANSFER"] = "manual";
    PaymentMethod["INTERNAL_SETTLEMENT"] = "internal_settlement";
    PaymentMethod["REVERSAL"] = "reversal";
})(PaymentMethod || (PaymentMethod = {}));
var IncomingPaymentMethod;
(function (IncomingPaymentMethod) {
    IncomingPaymentMethod["MANUAL_TRANSFER"] = "manual";
    IncomingPaymentMethod["DIRECT_DEBIT"] = "direct_debit";
})(IncomingPaymentMethod || (IncomingPaymentMethod = {}));

var LegalEntities;
(function (LegalEntities) {
    LegalEntities["SOLE_PROPRIETORSHIP"] = "sole_proprietorship";
    LegalEntities["ORGANISATION"] = "organisation";
    LegalEntities["PARTNERSHIP_UNDER_FIRM"] = "partnership_under_firm";
    LegalEntities["PRIVATE_COMPANY"] = "private_company";
    LegalEntities["LIMITED_COMPANY"] = "limited_company";
    LegalEntities["UNION"] = "union";
    LegalEntities["FOUNDATION"] = "foundation";
    LegalEntities["COOPERATION"] = "cooperation";
    LegalEntities["LIMITED_PARTNERSHIP"] = "limited_partnership";
})(LegalEntities || (LegalEntities = {}));

var PreferredChannel;
(function (PreferredChannel) {
    PreferredChannel["LETTER"] = "letter";
    PreferredChannel["EMAIL"] = "email";
})(PreferredChannel || (PreferredChannel = {}));

var ProductDuration;
(function (ProductDuration) {
    ProductDuration["ONE"] = "1";
    ProductDuration["TWO"] = "2";
    ProductDuration["THREE"] = "3";
    ProductDuration["FIVE"] = "5";
    ProductDuration["FLEX"] = "flex";
})(ProductDuration || (ProductDuration = {}));

var ProductType;
(function (ProductType) {
    ProductType["ELECTRICITY"] = "electricity";
    ProductType["GAS"] = "gas";
})(ProductType || (ProductType = {}));
var EnergyType;
(function (EnergyType) {
    EnergyType["ELECTRICITY"] = "electricity";
    EnergyType["GAS"] = "gas";
})(EnergyType || (EnergyType = {}));

/**
 * Enum to indicate RegisterType
 */
var RegisterType;
(function (RegisterType) {
    RegisterType["SUPPLY_LOW"] = "levering_laag";
    RegisterType["SUPPLY_NORMAL"] = "levering_normaal";
    RegisterType["BACK_SUPPLY_LOW"] = "teruglevering_laag";
    RegisterType["BACK_SUPPLY_NORMAL"] = "teruglevering_normaal";
    RegisterType["GAS"] = "levering_gas";
})(RegisterType || (RegisterType = {}));

var SegmentType;
(function (SegmentType) {
    SegmentType["HH"] = "household";
    SegmentType["SOHO"] = "small_office_home_office";
    SegmentType["SME"] = "small_medium_enterprise";
    SegmentType["RENT"] = "rentals";
    SegmentType["SPEC"] = "special";
})(SegmentType || (SegmentType = {}));

/**
 * Status of the HTTP call.
 *
 * Used by:
 * * [AsyncState](../interfaces/AsyncState.html)
 *
 * ## Example
 *
 * ```html
 * <div *ngIf="state$ | async as state">
 *     <div *ngIf="state.status === Status.IDLE">
 *         Waiting to be called
 *     </div>
 *     <div *ngIf="state.status === Status.PENDING">
 *         Loading
 *     </div>
 *     <div *ngIf="state.status === Status.SUCCESS">
 *         Data from server: {{ state.data }}
 *     </div>
 *     <div *ngIf="state.status === Status.ERROR">
 *         Error from server: {{ state.error }}
 *     </div>
 * </div>
 * ```
 */
var Status;
(function (Status) {
    Status["SUCCESS"] = "SUCCESS";
    Status["ERROR"] = "ERROR";
    Status["PENDING"] = "PENDING";
    Status["IDLE"] = "IDLE";
})(Status || (Status = {}));

var TariffType;
(function (TariffType) {
    TariffType["NORMAL"] = "N";
    TariffType["LOW"] = "L";
    TariffType["REDELIVERY"] = "T";
})(TariffType || (TariffType = {}));

var UnitOfMeasurement;
(function (UnitOfMeasurement) {
    UnitOfMeasurement["KILOWATT_HOUR"] = "kwh";
    UnitOfMeasurement["CUBIC_METERS"] = "m3";
})(UnitOfMeasurement || (UnitOfMeasurement = {}));

var EndContractReason;
(function (EndContractReason) {
    EndContractReason["VACANCY"] = "vacancy";
    EndContractReason["DIVORCE"] = "divorce";
    EndContractReason["EMIGRATION"] = "emigration";
    EndContractReason["CHANGED_TERMS"] = "changed_terms";
    EndContractReason["LIVING_IN_WITH_CUSTOMER"] = "living_in_with_customer";
    EndContractReason["LIVING_IN_WITH_NON_CUSTOMER"] = "living_in_with_non_customer";
    EndContractReason["NEW_ADDRESS_ALREADY_REGISTERED"] = "new_address_already_registered";
    EndContractReason["CANCEL_IN_COOLDOWN"] = "cancel_in_cooldown";
    EndContractReason["OVERLAP_SITUATION"] = "overlap_situation";
    EndContractReason["CANCEL_ONE_PRODUCT"] = "cancel_one_product";
    EndContractReason["DECEASED"] = "deceased";
    EndContractReason["TAKEOVER_OR_COMPANY_CLOSURE"] = "takeover_or_company_closure";
    EndContractReason["TAKEOVER_CONTRACT"] = "takeover_contract";
    EndContractReason["HH_TO_SOHO"] = "hh_to_soho";
    EndContractReason["HH_OR_SOHO_TO_SME"] = "hh_or_soho_to_sme";
    EndContractReason["SME_TO_HH_OR_SOHO"] = "sme_to_hh_or_soho";
    EndContractReason["NEW_ADDRESS_INCLUDES_ENERGY_COSTS"] = "new_address_includes_energy_costs";
    EndContractReason["MOVEOUT_WITHOUTREASON"] = "moveout_withoutreason";
    EndContractReason["BANKRUPTCY"] = "bankruptcy";
    EndContractReason["RECOVERY_ACTION"] = "recovery_action";
})(EndContractReason || (EndContractReason = {}));

var ContractType;
(function (ContractType) {
    ContractType["FIXED"] = "fixed";
    ContractType["DYNAMIC"] = "dynamic";
    ContractType["VARIABLE"] = "variable";
})(ContractType || (ContractType = {}));

const getAsyncStateActions = (...args) => {
    if (isApiActionsObject(args[0])) {
        return {
            ...args[0],
            clearActions: [args[0].clearAction, ...ensureArray(args[1])]
        };
    }
    else {
        return {
            requestAction: args[0],
            successAction: args[1],
            errorAction: args[2],
            clearActions: ensureArray(args[3])
        };
    }
};
/**
 * Shorthand for basic reducer to implement actions from CoreModules
 *
 * Example implementation:
 * ```typescript
    export const reducer = createAsyncStateReducer(
        getAction,
        getActionSuccess,
        getActionError,
        [getActionClear, clear]
    );
    ```
 **/
function createAsyncStateReducer(...args) {
    const { clearActions, errorAction, successAction, requestAction } = getAsyncStateActions(...args);
    const initialState = createInitialAsyncState();
    return createReducer(initialState, on(requestAction, (_, { actionId }) => ({
        actionId,
        status: Status.PENDING
    })), on(successAction, (_, { payload, actionId }) => ({
        status: Status.SUCCESS,
        actionId,
        data: payload
    })), on(errorAction, (_, { payload, actionId }) => ({
        status: Status.ERROR,
        error: payload,
        actionId
    })), ...clearActions.map(a => on(a, () => initialState)));
}
/** Creates the initial state for the `createAsyncStateReducer`. */
const createInitialAsyncState = () => {
    return { status: Status.IDLE };
};
function combineAsyncStatus(...statusList) {
    if (statusList.some(status => status === Status.ERROR)) {
        return Status.ERROR;
    }
    if (statusList.some(status => status === Status.PENDING)) {
        return Status.PENDING;
    }
    if (statusList.every(status => status === Status.SUCCESS)) {
        return Status.SUCCESS;
    }
    return Status.IDLE;
}
const getData = ({ data }) => data;
const getError = ({ error }) => error;
const getStatus = ({ status }) => status;

const ensureFunctionalErrors = (response) => ensureArray(response?.error?.errors || response?.error?.error || []);

/**
 * Checks if a HTTP error response contains a functional error code
 */
const containsMessage = (response, code) => ensureFunctionalErrors(response).some(functionalError => functionalError.code === code);

/**
 * Generated bundle index. Do not edit.
 */

export { API_PREFIX, BaseEffect, CLIENT_KEY, CLIENT_SECRET, CaseStatus, Channel, CommonCoreModule, ContractType, DEFAULT_REQUEST_STRATEGY, Direction, DirectionTariff, DirectionTariffZone, DocumentType, EndContractReason, EnergyType, FinancialItemStatus, FinancialItemType, Gender, HandleRequestService, IncomingPaymentMethod, LegalEntities, PaymentMethod, PreferredChannel, ProductDuration, ProductType, RegisterType, RequestStrategy, SegmentType, Status, TariffType, UnitOfMeasurement, accountIdHeaderName, baseEffectFactory, camelCase, camelCaseWhen, clearAll, combineAsyncStatus, combineEffectsWithProviders, containsMessage, convertNullMembersToUndefined, convertStringToEnumValue, createApi, createApiActions, createAsyncStateReducer, createBaseAction, createEffectWithProviders, createInitialAsyncState, createNamedApi, createNamedApiActions, createService, createServiceApi, ensureFunctionalErrors, getData, getError, getStatus, handleRequestWithStrategy, serviceTypes, snakeCase, snakeCaseWhen, toCamelcase, toHttpParams, toSnakeCase };
